import { STUDIO_SOURCE_MODE } from 'src/components/star-grid/reportStudioConstants';
import { STUDIO_SCRATCH_ID } from 'src/components/star-grid/hooks/useStudioSelection';
import { prepareStudioTemplate } from 'src/components/star-grid/utils/studioContext';
import type { StudioCatalog, StudioTemplate } from 'src/components/star-grid/reportStudio.types';

/**
 * A from-scratch starting point: the SAME studio screen a template opens, with nothing selected.
 *
 * Every component is present — palette, Filters/Rows/Columns shelves, layout preview — so the report
 * is built by dragging, not by filling in a separate form. Replaces the old Formik builder, which was
 * a second page with its own "Create Report" heading stacked under this one, and which arrived with a
 * row dimension already chosen.
 *
 * MEC is the one selection made for you: every deck-backed report has to name a deck before its rows
 * or columns mean anything, and MEC is the month-end deck each template also defaults to. Filters,
 * rows and columns start empty -- those are the report, and they are what the user drags in.
 *
 * The dataset is the catalog's first (the P&L cube) because the palette's dimensions come from a
 * dataset — a report with no dataset has nothing to drag.
 */
// The month-end deck. Matched case-insensitively: deck IDS are lowercase (`mec`) while the label the
// UI shows is upper case, so an exact match silently fell through to the first deck in the catalog
// ("current plan (monthly)") instead.
const SCRATCH_DECK = 'mec';

/** `datasetId` and `decks` reopen a saved report that no template fits on its own dataset and decks. */
export const scratchStudioTemplate = (catalog: StudioCatalog, datasetId?: string, decks: readonly string[] = []): StudioTemplate => {
  // Fall back to whatever the catalog does offer rather than pinning a deck that is not in it.
  const deck = decks[0] ?? catalog.deck_types?.find((type) => type.toLowerCase() === SCRATCH_DECK) ?? catalog.deck_types?.[0] ?? SCRATCH_DECK;
  const deckTypes = decks.length ? [...decks] : [deck];
  return prepareStudioTemplate({
    catalog,
    // No parameters: a template's parameters are what become preselected filter chips.
    parameters: [],
    definition: {
      schema_version: 1,
      dataset_id: datasetId ?? catalog.datasets[0]?.id ?? '',
      template_id: STUDIO_SCRATCH_ID,
      template_version: '1',
      // A report always reads SOME data vintage; Live (the catalog's first) is that, not a content
      // choice the user has to make before they can drag anything.
      default_version_id: catalog.versions[0]?.id ?? '',
      active_deck: deck,
      deck_types: deckTypes,
      // The deck exists with EMPTY axes: the shelves need a keyed deck to drop onto, but nothing is
      // preselected in it.
      decks: Object.fromEntries(deckTypes.map((type) => [type, { rows: [], filters: [], columns: [], line_sets: [] }])),
      parameters: {},
      fixed_context: {},
      source_mode: STUDIO_SOURCE_MODE,
      suppress_zero_rows: true
    }
  });
};
